import { useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { TOOLTIP_ID } from './constants';
import { calculateTooltipPosition } from './logic';
import { TooltipPresentation } from './presentation';

type Props = {
  /** 表示トリガーとなった要素。null の間は非表示 */
  anchorEl: HTMLElement | null;
  /** 表示する内容 */
  content: React.ReactNode;
};

const INITIAL_POSITION = { top: 0, left: 0, placement: 'top' as const, arrowOffsetX: 0 };

/**
 * document.body に portal 描画される共有ツールチップバブル。
 * TooltipProvider から anchorEl・content を受け取り、表示位置の計算のみを担う。
 */
export const Tooltip: React.FC<Props> = ({ anchorEl, content }) => {
  const isOpen = anchorEl !== null;
  const [position, setPosition] = useState(INITIAL_POSITION);
  const contentRef = useRef<HTMLSpanElement>(null);

  /**
   * 表示位置の計算。バブルの実際の幅・高さを計測してから位置を決める必要があり、
   * 描画結果を計測してから同期的に反映するため useLayoutEffect を使用する。
   */
  // biome-ignore lint/correctness/useExhaustiveDependencies: content はエフェクト本体で直接参照しないが、content が変わるとバブルの実測幅・高さも変わるため再計測のトリガーとして依存に含める
  useLayoutEffect(() => {
    if (!anchorEl) return;
    const anchorRect = anchorEl.getBoundingClientRect();
    setPosition(
      calculateTooltipPosition({
        anchorRect,
        bubbleWidth: contentRef.current?.offsetWidth ?? 0,
        bubbleHeight: contentRef.current?.offsetHeight ?? 0,
        // 縦スクロールバー幅を除いた可視領域の幅。window.innerWidth はスクロールバー幅を含むため、
        // 右端 clamp 時にバブルがスクロールバー下へ潜り込んで見切れる
        viewportWidth: document.documentElement.clientWidth,
      })
    );
  }, [anchorEl, content]);

  return createPortal(
    <TooltipPresentation
      ref={contentRef}
      tooltipId={TOOLTIP_ID}
      content={content}
      position={position}
      isOpen={isOpen}
    />,
    document.body
  );
};
